import {Inject, Injectable} from '@angular/core';
import {Command} from '@application/command/command';
import {BackendErrorCodeEnum} from '@application/helper/backend-error-code.enum';
import {BackendErrorContext} from '@application/helper/backend-error-context';
import {DateRange} from '@domain/date-range';
import {CalendarItem} from '@domain/planning/calendar-item';
import {CalendarItemGroup} from '@domain/planning/calendar-item-group';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningMachineRun} from '@domain/planning/planning-machine-run';
import {PlanningOrder} from '@domain/planning/planning-order';
import {Planning, PLANNING} from '@infrastructure/http/planning/planning';
import {ProductionOrdersWeaving, PRODUCTION_ORDERS_WEAVING} from '@infrastructure/http/production-order-weaving/production-orders-weaving';
import {ProductionOrders, PRODUCTION_ORDERS} from '@infrastructure/http/production-order/production-orders';
import {HttpTuftProductionOrdersService} from '@infrastructure/http/tuft-production-order/http-tuft-production-orders.service';
import {BackendError, DialogBuilderFactoryService, DialogOptions, DialogType, moment, TimeUtils, TranslateService} from '@vdw/angular-component-library';
import {cloneDeep, each, find, first, isEmpty, isEqual, isNil, join, lowerCase, noop, pull, remove, size} from 'lodash-es';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {ShowProductionOrderIncompatibleWithMachineAlertCommand} from '../show-production-order-incompatible-with-machine-alert/show-production-order-incompatible-with-machine-alert-command';
import {AddNewPlanningItemCommand} from './add-new-planning-item/add-new-planning-item-command';
import {AddNewPlanningItemParameters} from './add-new-planning-item/add-new-planning-item-parameters';
import {AddPlannedOrderToNewRunCommand} from './add-planned-order-to-new-run/add-planned-order-to-new-run-command';
import {AddPlannedPlanningOrderToNewRunParameters} from './add-planned-order-to-new-run/add-planned-order-to-new-run-parameters';
import {AddUnplannedOrderToNewRunCommand} from './add-unplanned-order-to-new-run/add-unplanned-order-to-new-run-command';
import {AddUnplannedOrderToNewRunParameters} from './add-unplanned-order-to-new-run/add-unplanned-order-to-new-run-parameters';
import {AddUnplannedOrderToRunCommand} from './add-unplanned-order-to-run/add-unplanned-order-to-run-command';
import {AddUnplannedOrderToRunParameters} from './add-unplanned-order-to-run/add-unplanned-order-to-run-parameters';
import {PlanningScheduler} from './planning-scheduler';
import {RemovePlanningItemsOutsideDateRangeCommand} from './remove-planning-items-outside-date-range/remove-planning-items-outside-date-range-command';
import {RemovePlanningOrderCommand} from './remove-planning-order/remove-planning-order-command';
import {RemovePlanningOrderParameters} from './remove-planning-order/remove-planning-order-parameters';
import {RescheduleItemsCommand} from './reschedule-items/reschedule-items-command';
import {RescheduleItemsParameters} from './reschedule-items/reschedule-items-parameters';
import {ReschedulePlanningOrderCommand} from './reschedule-planning-order/reschedule-planning-order-command';
import {ReschedulePlanningOrderParameters} from './reschedule-planning-order/reschedule-planning-order-parameters';

enum PlanningAction {
  GET_PLANNING,
  RESCHEDULE_ITEMS,
  RESCHEDULE_PLANNING_ORDER,
  ADD_NEW_PLANNING_ITEM,
  REMOVE_PLANNING_ORDER,
  ADD_UNPLANNED_PLANNING_ORDER_TO_RUN,
  ADD_UNPLANNED_PLANNING_ORDER_TO_NEW_RUN,
  ADD_PLANNED_PLANNING_ORDER_TO_NEW_RUN
}

interface QueuedPlanningAction {
  isProcessing: boolean;
  action: PlanningAction;
  parameters: any;
  onSuccess: () => void;
  onError: () => void;
}

@Injectable()
export class PlanningSchedulerService implements PlanningScheduler {
  private queuedPlanningActions: QueuedPlanningAction[] = [];
  private definedPlanningActions: Map<PlanningAction, (parameters: any, onSuccess: () => void, onError: () => void) => void>;
  private planningChangesSubject: BehaviorSubject<CalendarItemGroup[]> = new BehaviorSubject<CalendarItemGroup[]>([]);
  private selectedDateRange: DateRange;
  private groupedCalendarItems: CalendarItemGroup[] = [];
  private getPlanningSubscription: Subscription;

  private readonly rescheduleItemsCommand = new RescheduleItemsCommand();
  private readonly reschedulePlanningOrderCommand = new ReschedulePlanningOrderCommand();
  private readonly addNewPlanningItemCommand = new AddNewPlanningItemCommand();
  private readonly removePlanningOrderCommand = new RemovePlanningOrderCommand();
  private readonly removePlanningItemsOutsideDateRangeCommand = new RemovePlanningItemsOutsideDateRangeCommand();
  private readonly addUnplannedOrderToRunCommand = new AddUnplannedOrderToRunCommand();
  private readonly addUnplannedOrderToNewRunCommand = new AddUnplannedOrderToNewRunCommand();
  private readonly addPlannedOrderToNewRunCommand = new AddPlannedOrderToNewRunCommand();

  private readonly generalErrorTranslationKey = 'GENERAL.ERRORS.SOMETHING_WRONG';
  private readonly updatePlanningErrorTranslationKey = 'PLANNING.ERRORS.UPDATE_PLANNING';

  public isUpdating = false;

  public constructor(
    @Inject(PLANNING) private readonly planning: Planning,
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    @Inject(PRODUCTION_ORDERS_WEAVING) private readonly productionOrdersWeaving: ProductionOrdersWeaving,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly showProductionOrderIncompatibleWithMachineAlertCommand: ShowProductionOrderIncompatibleWithMachineAlertCommand,
    private readonly productionOrdersTufting: HttpTuftProductionOrdersService
  ) {
    this.initializePlanningActions();
  }

  public onPlanningChanges(): Observable<CalendarItemGroup[]> {
    return this.planningChangesSubject;
  }

  public getPlanning(selectedDateRange: DateRange, onSuccess: () => void = noop): void {
    this.selectedDateRange = selectedDateRange;

    if (!isNil(this.getPlanningSubscription)) {
      this.getPlanningSubscription.unsubscribe();
    }
    remove(this.queuedPlanningActions, {action: PlanningAction.GET_PLANNING});

    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.GET_PLANNING,
      parameters: this.selectedDateRange,
      onSuccess,
      onError: noop
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public rescheduleItems(planningItem: PlanningItem, sourceMachine: PlanningMachine, destinationGroupedCalendarItem: CalendarItemGroup, destinationMachine: PlanningMachine): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.RESCHEDULE_ITEMS,
      parameters: {planningItem, sourceMachine, destinationGroupedCalendarItem, destinationMachine} as RescheduleItemsParameters,
      onSuccess: () => (this.isUpdating = false),
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public reschedulePlanningOrder(
    draggedPlanningOrder: PlanningOrder,
    sourceRun: PlanningMachineRun,
    destinationRun: PlanningMachineRun,
    planningOrderNewPosition: number,
    destinationMachine: PlanningMachine,
    sourceMachineId: number
  ): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.RESCHEDULE_PLANNING_ORDER,
      parameters: {draggedPlanningOrder, sourceRun, destinationRun, planningOrderNewPosition, destinationMachine, sourceMachineId} as ReschedulePlanningOrderParameters,
      onSuccess: () => (this.isUpdating = false),
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public addNewPlanningItem(newPlanningItem: PlanningItem, destinationGroupedCalendarItem: CalendarItemGroup): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.ADD_NEW_PLANNING_ITEM,
      parameters: {newPlanningItem, destinationGroupedCalendarItem} as AddNewPlanningItemParameters,
      onSuccess: () => (this.isUpdating = false),
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public resetPlanning(): void {
    this.planningChangesSubject.next(cloneDeep(this.groupedCalendarItems));
  }

  public getCachedGroupedCalendarItem(machineId: number): CalendarItemGroup {
    return cloneDeep(find(this.groupedCalendarItems, {machineId}));
  }

  public setGroupedCalendarItems(groupedCalendarItems: CalendarItemGroup[]): void {
    this.groupedCalendarItems = cloneDeep(groupedCalendarItems);
  }

  public addUnplannedPlanningOrderToRun(unplannedPlanningOrder: PlanningOrder, run: PlanningMachineRun, planningOrderPosition: number, machine: PlanningMachine, onSuccess: () => void): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.ADD_UNPLANNED_PLANNING_ORDER_TO_RUN,
      parameters: {unplannedPlanningOrder, run, planningOrderPosition, machine} as AddUnplannedOrderToRunParameters,
      onSuccess: () => {
        this.isUpdating = false;
        return onSuccess();
      },
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public addUnplannedPlanningOrderToNewRun(unplannedPlanningOrder: PlanningOrder, machine: PlanningMachine, onSuccess: () => void): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.ADD_UNPLANNED_PLANNING_ORDER_TO_NEW_RUN,
      parameters: {unplannedPlanningOrder, machine} as AddUnplannedOrderToNewRunParameters,
      onSuccess: () => {
        this.isUpdating = false;
        return onSuccess();
      },
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  public addPlannedPlanningOrderToNewRun(plannedPlanningOrder: PlanningOrder, sourceRun: PlanningMachineRun, sourceMachineId: number, destinationMachine: PlanningMachine): void {
    this.queuedPlanningActions.push({
      isProcessing: false,
      action: PlanningAction.ADD_PLANNED_PLANNING_ORDER_TO_NEW_RUN,
      parameters: {plannedPlanningOrder, sourceRun, sourceMachineId, destinationMachine} as AddPlannedPlanningOrderToNewRunParameters,
      onSuccess: () => (this.isUpdating = false),
      onError: this.showAlertDialogForUpdatePlanningError()
    });

    this.processFirstItemInQueuedPlanningActions();
  }

  private processFirstItemInQueuedPlanningActions(): void {
    const firstItemInQueue: QueuedPlanningAction = first(this.queuedPlanningActions);

    if (!isNil(firstItemInQueue) && !firstItemInQueue.isProcessing) {
      firstItemInQueue.isProcessing = true;
      const {action, parameters, onSuccess, onError} = firstItemInQueue;

      this.definedPlanningActions.get(action)(parameters, onSuccess, onError);
    }
  }

  private cleanUpAndHandleNextItemInQueue(): void {
    pull(this.queuedPlanningActions, find(this.queuedPlanningActions, {isProcessing: true}));

    this.processFirstItemInQueuedPlanningActions();
  }

  private getGroupedCalendarItemsToPatch(groupedCalendarItems: CalendarItemGroup[], newGroupedCalendarItems: CalendarItemGroup[]): CalendarItemGroup[] {
    const groupedCalendarItemsToPatch = [];
    each(newGroupedCalendarItems, (calendarItemGroup: CalendarItemGroup, groupIndex: number) => {
      if (!isEqual(calendarItemGroup.items, groupedCalendarItems[groupIndex].items)) {
        groupedCalendarItemsToPatch.push(new CalendarItemGroup(calendarItemGroup.machineId, []));
        each(calendarItemGroup.items, (item: CalendarItem, itemIndex: number) => {
          if (!isEqual(item, groupedCalendarItems[groupIndex].items[itemIndex])) {
            groupedCalendarItemsToPatch[size(groupedCalendarItemsToPatch) - 1].items.push(item);
          }
        });
      }
    });
    return groupedCalendarItemsToPatch;
  }

  private showAlertDialog(options: DialogOptions): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog(options);
  }

  private initializePlanningActions(): void {
    this.definedPlanningActions = new Map<PlanningAction, (parameters: any, onSuccess: () => void, onError: () => void) => void>([
      [
        PlanningAction.GET_PLANNING,
        (parameters: DateRange, onSuccess: () => void): void => {
          this.getPlanningSubscription = this.planning.getGroupedRunsForPeriod(parameters).subscribe((groupedCalendarItems: CalendarItemGroup[]) => {
            this.groupedCalendarItems = groupedCalendarItems;
            this.planningChangesSubject.next(cloneDeep(groupedCalendarItems));

            onSuccess();
            this.cleanUpAndHandleNextItemInQueue();
          });
        }
      ],
      [
        PlanningAction.RESCHEDULE_ITEMS,
        (parameters: RescheduleItemsParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          const {planningItem, sourceMachine, destinationMachine} = parameters;

          if (planningItem instanceof PlanningMachineRun && !isEqual(sourceMachine.id, destinationMachine.id)) {
            const originalParameters = cloneDeep(parameters);
            const groupedCalendarItems = this.executeCommand(this.rescheduleItemsCommand, parameters);
            this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);

            this.checkRunCompatibilityWithMachine(originalParameters, onSuccess, onError);
          } else {
            this.executeCommandAndUpdateGroupedCalendarItems(this.rescheduleItemsCommand, parameters, onSuccess, onError);
          }
        }
      ],
      [
        PlanningAction.RESCHEDULE_PLANNING_ORDER,
        (parameters: ReschedulePlanningOrderParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          const {destinationMachine, sourceMachineId} = parameters;

          if (isEqual(destinationMachine.id, sourceMachineId)) {
            this.executeCommandAndUpdateGroupedCalendarItems(this.reschedulePlanningOrderCommand, parameters, onSuccess, onError);
          } else {
            const originalParameters = cloneDeep(parameters);
            const groupedCalendarItems = this.executeCommand(this.reschedulePlanningOrderCommand, parameters);

            this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);

            this.checkProductionOrderCompatibilityWithMachine(
              parameters.draggedPlanningOrder,
              destinationMachine,
              (planningOrder: PlanningOrder): CalendarItemGroup[] => {
                originalParameters.draggedPlanningOrder = planningOrder;
                return this.executeCommand(this.reschedulePlanningOrderCommand, originalParameters);
              },
              onSuccess,
              onError
            );
          }
        }
      ],
      [
        PlanningAction.ADD_NEW_PLANNING_ITEM,
        (parameters: AddNewPlanningItemParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          return this.executeCommandAndUpdateGroupedCalendarItems(this.addNewPlanningItemCommand, parameters, onSuccess, onError);
        }
      ],
      [
        PlanningAction.REMOVE_PLANNING_ORDER,
        (parameters: RemovePlanningOrderParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          this.executeCommandAndUpdateGroupedCalendarItems(this.removePlanningOrderCommand, parameters, onSuccess, onError);
        }
      ],
      [
        PlanningAction.ADD_UNPLANNED_PLANNING_ORDER_TO_RUN,
        (parameters: AddUnplannedOrderToRunParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          const originalParameters = cloneDeep(parameters);
          const groupedCalendarItems = this.executeCommand(this.addUnplannedOrderToRunCommand, parameters);
          const {unplannedPlanningOrder, machine} = parameters;

          this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);

          this.checkProductionOrderCompatibilityWithMachine(
            unplannedPlanningOrder,
            machine,
            (planningOrder: PlanningOrder): CalendarItemGroup[] => {
              originalParameters.unplannedPlanningOrder = planningOrder;

              return this.executeCommand(this.addUnplannedOrderToRunCommand, originalParameters);
            },
            onSuccess,
            onError
          );
        }
      ],
      [
        PlanningAction.ADD_UNPLANNED_PLANNING_ORDER_TO_NEW_RUN,
        (parameters: AddUnplannedOrderToNewRunParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          const originalParameters = cloneDeep(parameters);
          const groupedCalendarItems = this.executeCommand(this.addUnplannedOrderToNewRunCommand, parameters);
          const {unplannedPlanningOrder, machine} = parameters;

          this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);

          this.checkProductionOrderCompatibilityWithMachine(
            unplannedPlanningOrder,
            machine,
            (planningOrder: PlanningOrder): CalendarItemGroup[] => {
              originalParameters.unplannedPlanningOrder = planningOrder;
              return this.executeCommand(this.addUnplannedOrderToNewRunCommand, originalParameters);
            },
            onSuccess,
            onError
          );
        }
      ],
      [
        PlanningAction.ADD_PLANNED_PLANNING_ORDER_TO_NEW_RUN,
        (parameters: AddPlannedPlanningOrderToNewRunParameters, onSuccess: () => void, onError: () => void): void => {
          this.isUpdating = true;
          const {sourceMachineId, destinationMachine} = parameters;

          if (isEqual(sourceMachineId, destinationMachine.id)) {
            this.executeCommandAndUpdateGroupedCalendarItems(this.addPlannedOrderToNewRunCommand, parameters, onSuccess, onError);
          } else {
            const originalParameters = cloneDeep(parameters);
            const groupedCalendarItems = this.executeCommand(this.addPlannedOrderToNewRunCommand, parameters);

            this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);

            this.checkProductionOrderCompatibilityWithMachine(
              parameters.plannedPlanningOrder,
              destinationMachine,
              (planningOrder: PlanningOrder) => {
                originalParameters.plannedPlanningOrder = planningOrder;
                return this.executeCommand(this.addPlannedOrderToNewRunCommand, originalParameters);
              },
              onSuccess,
              onError
            );
          }
        }
      ]
    ]);
  }

  private executeCommand<T, U>(command: Command<T, U>, parameters: T): U {
    parameters['groupedCalendarItems'] = cloneDeep(this.groupedCalendarItems);

    return command.execute(parameters);
  }

  private executeCommandAndUpdateGroupedCalendarItems<T>(command: Command<T, CalendarItemGroup[]>, parameters: T, onSuccess: () => void, onError: () => void): void {
    const groupedCalendarItems = this.executeCommand(command, parameters);

    const groupedCalendarItemsToPatch = this.getGroupedCalendarItemsToPatch(this.groupedCalendarItems, groupedCalendarItems);

    if (!isEmpty(groupedCalendarItemsToPatch)) {
      this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);
      this.updateGroupedCalendarItems(groupedCalendarItemsToPatch, onSuccess, onError);
    } else {
      this.cleanUpAndHandleNextItemInQueue();
    }
  }

  private updateGroupedCalendarItems(groupedCalendarItems: CalendarItemGroup[], onSuccess: () => void, onError: () => void): void {
    this.planning
      .updateGroupedItems(groupedCalendarItems)
      .pipe(finalize(() => this.cleanUpAndHandleNextItemInQueue()))
      .subscribe({
        next: () => onSuccess(),
        error: () => {
          this.resetPlanning();
          onError();
        }
      });
  }

  private checkProductionOrderCompatibilityWithMachine(
    planningOrder: PlanningOrder,
    machine: PlanningMachine,
    executeCommand: (planningOrder: PlanningOrder) => CalendarItemGroup[],
    onSuccess: () => void,
    onError: () => void
  ): void {
    let checkCompatibilityWithMachineResult: Observable<number>;
    if (planningOrder.isCarpetOrder) {
      checkCompatibilityWithMachineResult = this.productionOrders.checkCompatibilityWithMachine(planningOrder.productionOrder.id, machine.id);
    } else if (planningOrder.isWeavingOrder) {
      checkCompatibilityWithMachineResult = this.productionOrdersWeaving.checkCompatibilityWithMachine(planningOrder.productionOrder.id, machine.id);
    } else if (planningOrder.isTuftingOrder) {
      checkCompatibilityWithMachineResult = this.productionOrdersTufting.checkCompatibilityWithMachine(planningOrder.productionOrder.id, machine.id);
    }

    checkCompatibilityWithMachineResult?.subscribe({
      next: (durationInMilliseconds: number) => {
        planningOrder.endDate = moment(planningOrder.startDate).add(durationInMilliseconds, TimeUtils.MILLISECONDS_UNIT).toDate();

        const groupedCalendarItems = executeCommand(planningOrder);
        const groupedCalendarItemsToPatch = this.getGroupedCalendarItemsToPatch(this.groupedCalendarItems, groupedCalendarItems);

        this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);
        this.updateGroupedCalendarItems(groupedCalendarItemsToPatch, onSuccess, onError);
      },
      error: (error: BackendError) => {
        const errorCode = error.errorContext?.errorCode as string;
        if (BackendErrorCodeEnum[errorCode] === BackendErrorCodeEnum.LINKED_ENTITIES) {
          this.isUpdating = false;
          this.showProductionOrderIncompatibleWithMachineAlertCommand.execute({
            error,
            productionOrderName: planningOrder.productionOrder.name,
            machineName: machine.name
          });
        } else {
          this.showAlertDialog({titleText: this.generalErrorTranslationKey, messageText: this.updatePlanningErrorTranslationKey, type: DialogType.ERROR});
        }

        this.resetPlanning();
        this.cleanUpAndHandleNextItemInQueue();
      }
    });
  }

  private showAlertDialogForUpdatePlanningError(): () => void {
    return (): void => this.showAlertDialog({titleText: this.generalErrorTranslationKey, messageText: this.updatePlanningErrorTranslationKey, type: DialogType.ERROR});
  }

  private checkRunCompatibilityWithMachine(parameters: RescheduleItemsParameters, onSuccess: () => void, onError: () => void): void {
    const {destinationMachine} = parameters;
    const run = parameters.planningItem as PlanningMachineRun;

    this.planning.checkRunCompatibilityWithMachine(run.id, destinationMachine.id).subscribe({
      next: (runDurationInMilliseconds: number) => {
        run.updateDuration(runDurationInMilliseconds);
        parameters.planningItem = run;

        const groupedCalendarItems = this.executeCommand(this.rescheduleItemsCommand, parameters);
        const groupedCalendarItemsToPatch = this.getGroupedCalendarItemsToPatch(this.groupedCalendarItems, groupedCalendarItems);

        this.removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems);
        this.updateGroupedCalendarItems(groupedCalendarItemsToPatch, onSuccess, onError);
      },
      error: (error: BackendError) => {
        if (isEqual(BackendErrorCodeEnum[(error.errorContext as BackendErrorContext).errorCode], BackendErrorCodeEnum.LINKED_ENTITIES)) {
          this.showAlertDialog({
            titleText: this.translate.instant('PLANNING.ERRORS.INCOMPATIBLE_ENTITY', {entity: lowerCase(this.translate.instant('PLANNING.ADD_ITEM.TYPES.RUN'))}),
            messageText: this.translate.instant('PLANNING.ERRORS.INCOMPATIBLE_RUN', {
              machineName: this.getMachineNameForCompatibilityAlertDialog(destinationMachine.name),
              compatibleMachinesList: join(error.errorContext.linkedEntityNames, ', ')
            }),
            type: DialogType.INFORMATION
          });
          this.isUpdating = false;
        } else {
          this.showAlertDialog({
            titleText: this.generalErrorTranslationKey,
            messageText: this.updatePlanningErrorTranslationKey,
            type: DialogType.ERROR
          });
        }

        this.resetPlanning();
        this.cleanUpAndHandleNextItemInQueue();
      }
    });
  }

  private getMachineNameForCompatibilityAlertDialog(machineName: string): string {
    return isEmpty(machineName) ? this.translate.instant('GENERAL.NO_DATA', {object: lowerCase(this.translate.instant('GENERAL.NAME'))}) : machineName;
  }

  private removePlanningItemsOutsideDateRangeAndEmitPlanningChanges(groupedCalendarItems: CalendarItemGroup[]): void {
    const updatedGroupedCalendarItems = cloneDeep(groupedCalendarItems);

    this.removePlanningItemsOutsideDateRangeCommand.execute({groupedCalendarItems: updatedGroupedCalendarItems, dateRange: this.selectedDateRange});
    this.planningChangesSubject.next(updatedGroupedCalendarItems);
  }
}
